import { Trans } from '@lingui/react/macro';
import type { ReactNode } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import type { AdvancedSettings } from '@/types/template';

interface RulesFormProps {
  advancedSettings: AdvancedSettings;
  onChange: (settings: AdvancedSettings) => void;
}

/**
 * The scheduling-rules controls for the Build workspace, shown inside
 * {@link RulesPopover}. Exposes the four rules rotagen enforces
 * (`restBreakBetweenShifts`, `splitShifts`, `minimumDaysOff` incl. consecutive,
 * and `maxConsecutiveDays`). The flat `AdvancedSettings` view is converted to
 * the canonical `SchedulingRuleset` by `scheduling-rules.adapter.ts`.
 */
export function RulesForm({ advancedSettings, onChange }: RulesFormProps) {
  function update<Key extends keyof AdvancedSettings>(key: Key, value: AdvancedSettings[Key]) {
    onChange({ ...advancedSettings, [key]: value });
  }

  return (
    <div className="space-y-4">
      <NumberRow
        id="rules-rest-break"
        label={<Trans>Rest break between shifts (hours)</Trans>}
        min={0}
        max={24}
        value={advancedSettings.overrideRestBreak}
        onChange={(value) => update('overrideRestBreak', value)}
      />

      <SwitchRow
        id="rules-allow-split-shifts"
        label={<Trans>Allow split shifts</Trans>}
        checked={advancedSettings.allowSplitShifts}
        onCheckedChange={(checked) => update('allowSplitShifts', checked)}
      />

      {advancedSettings.allowSplitShifts && (
        <NumberRow
          id="rules-max-split-shifts"
          label={<Trans>Max split shifts per day</Trans>}
          min={1}
          max={3}
          value={advancedSettings.maxSplitShiftsPerDay}
          onChange={(value) => update('maxSplitShiftsPerDay', value)}
        />
      )}

      <NumberRow
        id="rules-min-days-off"
        label={<Trans>Days off per week</Trans>}
        min={0}
        max={7}
        value={advancedSettings.minDaysOffPerWeek}
        onChange={(value) => update('minDaysOffPerWeek', value)}
      />

      <SwitchRow
        id="rules-consecutive-days-off"
        label={<Trans>Days off must be consecutive</Trans>}
        checked={advancedSettings.consecutiveDaysOff ?? false}
        onCheckedChange={(checked) => update('consecutiveDaysOff', checked)}
      />

      <NumberRow
        id="rules-max-consecutive-days"
        label={<Trans>Max consecutive days worked</Trans>}
        min={1}
        max={7}
        value={advancedSettings.maxConsecutiveDaysWorked}
        onChange={(value) => update('maxConsecutiveDaysWorked', value)}
      />
    </div>
  );
}

/** Labelled numeric row; an empty input clears the value (`undefined`). */
function NumberRow({
  id,
  label,
  min,
  max,
  value,
  onChange,
}: {
  id: string;
  label: ReactNode;
  min: number;
  max: number;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <Label htmlFor={id} className="flex-1">
        {label}
      </Label>
      <Input
        id={id}
        type="number"
        min={min}
        max={max}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
        className="w-20"
      />
    </div>
  );
}

/** Labelled toggle row. */
function SwitchRow({
  id,
  label,
  checked,
  onCheckedChange,
}: {
  id: string;
  label: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <Label htmlFor={id} className="flex-1">
        {label}
      </Label>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}
